import { useEffect, useMemo } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  getCompanySettings as getCompanySettingsService,
  updateCompanySettings as updateCompanySettingsService,
} from "services/CompanySettings.services";
import {
  addCustomTemplate as addCustomTemplateService,
  downloadTemplate as downloadTemplateService,
  getTemplatePreview,
  getTemplates as getTemplatesService,
} from "services/Templates.Services";
import { toast } from "sonner";

import type { Template, TemplateResponse } from "types/Template.type";

import { queryClient } from "utils/queryClient";

export default function useFindingsTemplate() {
  const { t } = useTranslation();

  const getTemplates = useQuery<TemplateResponse>({
    queryKey: ["templates"],
    queryFn: getTemplatesService,
  });

  function useTemplatePreview(templateId: string, languageId: string) {
    return useQuery<string>({
      queryKey: ["templatePreview", templateId, languageId],
      queryFn: () => getTemplatePreview(templateId, languageId),
      enabled: !!templateId && !!languageId && templateId !== "100",
      retry: false,
    });
  }

  function useDownloadTemplate(templateId: string) {
    return useQuery<Blob>({
      queryKey: ["downloadTemplate", templateId],
      queryFn: () => downloadTemplateService(templateId),
      enabled: false,
    });
  }

  const addCustomTemplate = useMutation<any, Error, any>({
    mutationFn: (data) => addCustomTemplateService(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companySettings"] });
      toast.success("", { description: t("profile.findings.customTemplateAdded") });
    },
  });

  const updateTemplate = useMutation<any, Error, any>({
    mutationFn: (data) => updateCompanySettingsService(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companySettings"] });
      toast.success("", { description: t("profile.findings.customTemplateUpdated") });
    },
  });

  const getCompanySettings = useQuery<any>({
    queryKey: ["companySettings"],
    queryFn: getCompanySettingsService,
  });

  const form = useForm({
    defaultValues: {
      templateId: "1",
      languageId: "1",
    },
  });

  const {
    handleSubmit,
    control,
    watch,
    formState: { isDirty, isSubmitting },
  } = form;

  function onSubmit(data: any) {
    updateTemplate.mutate(data);
  }

  const templateId = watch("templateId");
  const languageId = watch("languageId");

  const { data: templatePreview } = useTemplatePreview(templateId, languageId);

  const downloadTemplate = useDownloadTemplate(templateId);

  const templates = useMemo(
    () =>
      getTemplates.data?.resultList.map((template: Template) => ({
        label: template.name,
        value: template.id.toString(),
      })) || [],
    [getTemplates.data?.resultList],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const isDocxMime =
      file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" || file.type === "";
    const isDocxExtension = file.name.toLowerCase().endsWith(".docx");

    if (!isDocxMime || !isDocxExtension) {
      toast.error("Only .docx files are allowed.");
      e.target.value = "";
      return;
    }

    addCustomTemplate.mutate({ file });
    e.target.value = "";
    //updateTemplate.mutate({ templateId: "100", languageId: languageId });
  };

  useEffect(() => {
    const templateId = getCompanySettings.data?.templateId?.toString();
    const languageId = getCompanySettings.data?.languageId?.toString();

    if (templateId && languageId) {
      form.reset({ templateId, languageId });
    }
  }, [getCompanySettings.data, form]);

  const handleDownload = async () => {
    const response = await downloadTemplate.refetch();
    if (response.data) {
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template.docx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }
  };

  return {
    form,
    handleSubmit,
    control,
    isDirty,
    isSubmitting,
    onSubmit,
    templateId,
    templatePreview,
    handleChange,
    handleDownload,
    templates,
    getCompanySettings,
  };
}
